import React, { useState, useContext } from "react";
import { FaChevronLeft, FaChevronRight, FaEdit } from "react-icons/fa";
import { Context } from "../../../Context";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-coverflow";
import { EffectCoverflow, Autoplay, Virtual } from "swiper/modules";
import { toast } from "react-toastify";
import { API, Storage } from "aws-amplify";
import Spoon from './images/spoon.png';
import SpoonR from './images/spoonR.png';

const Feedback = () => {
  const { institutionData, setInstitutionData } = useContext(Context);
  const { testimonials = [] } = institutionData;
  const [activeIndex, setActiveIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editTestimonials, setEditTestimonials] = useState([...testimonials]);
  const [imageFiles, setImageFiles] = useState({});

  const UserCtx = useContext(Context);
  const isAdmin = UserCtx.userData.userType === "admin";

  const handlePrev = () => {
    if (swiperInstance) swiperInstance.slidePrev();
  };

  const handleNext = () => {
    if (swiperInstance) swiperInstance.slideNext();
  };

  const handleTestimonialChange = (index, field, value) => {
    const updatedTestimonials = [...editTestimonials];
    updatedTestimonials[index] = { ...updatedTestimonials[index], [field]: value };
    setEditTestimonials(updatedTestimonials);
  };

  const handleAddTestimonial = () => {
    setEditTestimonials([...editTestimonials, { name: "", text: "", image: "" }]);
  };

  const handleDeleteTestimonial = (index) => {
    const updatedTestimonials = editTestimonials.filter((_, i) => i !== index);
    setEditTestimonials(updatedTestimonials);
    setImageFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[index];
      return newFiles;
    });
  };

  const handleTestimonialImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFiles(prev => ({ ...prev, [index]: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        handleTestimonialChange(index, "image", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImageToS3 = async (file, index) => {
    try {
      const fileName = `testimonials/${institutionData.institution}-${Date.now()}-${index}-${file.name}`;
      await Storage.put(fileName, file, {
        contentType: file.type,
        level: 'public'
      });
      const fileUrl = await Storage.get(fileName);
      return fileUrl.split('?')[0];
    } catch (error) {
      console.error(`Error uploading image for testimonial ${index} to S3:`, error);
      throw error;
    }
  };

  // Validation function to check if all required fields are filled
  const areRequiredFieldsFilled = () => {
    return editTestimonials.every(testimonial => 
      testimonial.name?.trim() &&
      testimonial.text?.trim() &&
      testimonial.image
    );
  };

  const handleSave = async () => {
    if (!areRequiredFieldsFilled()) {
      toast.error("Please fill in all required fields marked with *", {
        position: "top-right",
        autoClose: 3000,
        style: {
          background: "#EF4444",
          color: "#ffffff",
          borderRadius: "8px",
          padding: "12px",
        },
      });
      return;
    }

    try {
      setIsUploading(true);

      // Upload all new images first
      const uploadPromises = Object.entries(imageFiles).map(([index, file]) =>
        uploadImageToS3(file, index)
          .then(url => ({ index: parseInt(index), url }))
      );

      const uploadedImages = await Promise.all(uploadPromises);

      // Update testimonials with new image URLs
      const updatedTestimonials = editTestimonials.map((testimonial, index) => {
        const uploadedImage = uploadedImages.find(img => img.index === index);
        return uploadedImage
          ? { ...testimonial, image: uploadedImage.url }
          : testimonial;
      });

      // Prepare the updated data
      const updatedData = {
        ...institutionData,
        testimonials: updatedTestimonials
      };

      // Update local state
      setInstitutionData(updatedData);

      // Save to backend
      await API.put('main', `/admin/editable-ui/${institutionData.institution}?query=testimonial`, {
        body: { testimonials: updatedTestimonials }
      });

      toast.success("Testimonials saved successfully!", {
        position: "top-right",
        autoClose: 3000,
        style: {
          background: "#16A34A",
          color: "#ffffff",
          borderRadius: "8px",
          padding: "12px",
        },
      });

      setImageFiles({});
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving testimonials:", error);
      toast.error("Failed to save testimonials. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setEditTestimonials([...testimonials]);
    setImageFiles({});
    toast.info("Changes discarded.", {
      position: "top-right",
      autoClose: 3000,
      style: {
        background: "#722F37",
        color: "#ffffff",
        borderRadius: "8px",
        padding: "12px",
      },
    });
    setIsEditing(false);
  };

  if (testimonials.length === 0 && !isEditing) {
    return null;
  }

  return (
    <section className="bg-secondaryColor flex flex-col items-center px-3 md:px-8 py-0 sm:pt-5 pb-16 font-lora relative">
      <div className="absolute top-4 right-4 z-20">
        {isAdmin && (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-[var(--primary-color)] text-white px-4 py-2 rounded-full font-medium shadow-lg transition-all duration-300 flex items-center gap-2"
          >
            <FaEdit className="text-md" />
            <span className="text-md">Edit</span>
          </button>
        )}
      </div>

      <div className="w-full md:w-[80%] lg:w-[70%] text-center text-primaryColor">
        <h1 className="text-2xl sm:text-2xl md:text-4xl lg:text-5xl font-bold">
          Hear from those who know us Better
        </h1>
      </div>

      {isEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl p-6 max-h-[80vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Testimonials Section</h3>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Testimonial List */}
            <div className="space-y-4">
              {editTestimonials.map((testimonial, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-800">Testimonial #{index + 1}</h4>
                    <button
                      onClick={() => handleDeleteTestimonial(index)}
                      disabled={index === 0}
                      className={`text-red-500 ${index === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:text-red-700'}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-900">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          maxLength={50}
                          value={testimonial.name}
                          onChange={(e) => handleTestimonialChange(index, "name", e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">{testimonial.name.length}/50 characters</p>
                      </div>

                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-900">
                          Testimonial <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={testimonial.text}
                          onChange={(e) => handleTestimonialChange(index, "text", e.target.value)}
                          maxLength={150}
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          rows="4"
                        />
                        <p className="text-xs text-gray-500 mt-1">{testimonial.text.length}/150 characters</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-900">
                          Image <span className="text-red-500">*</span>
                        </label>
                        {testimonial.image && (
                          <img
                            src={testimonial.image}
                            alt="Preview"
                            className="w-24 h-24 object-cover rounded-lg mb-2"
                          />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleTestimonialImageUpload(index, e)}
                          className="text-xs text-gray-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button
                onClick={handleAddTestimonial}
                className="w-full py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Add Testimonial
              </button>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-center gap-3 mt-6">
              <button
                onClick={handleSave}
                disabled={isUploading || !areRequiredFieldsFilled()}
                className={`text-white bg-primaryColor focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primaryColor dark:focus:ring-blue-800 ${
                  isUploading || !areRequiredFieldsFilled() ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isUploading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      <Swiper
        modules={[EffectCoverflow, Autoplay, Virtual]}
        slidesPerView={1}
        spaceBetween={20}
        breakpoints={{
          640: { slidesPerView: 1.2, spaceBetween: 10 },
          768: { slidesPerView: 2, spaceBetween: 20 },
          1024: { slidesPerView: 2, spaceBetween: 20 },
          1280: { slidesPerView: 3, spaceBetween: 50 },
        }}
        centeredSlides={true}
        loop={testimonials.length > 1}
        onSwiper={setSwiperInstance}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        autoplay={{
          delay: 2500,
          reverseDirection: true,
          disableOnInteraction: false,
        }}
        className="h-[100%] w-[100%] md:w-[95%] lg:w-[90%] xl:w-[90%] mt-7 md:mt-16 flex justify-center"
      >
        {testimonials.map((testimonial, index) => (
          <SwiperSlide
            key={index}
            className="flex items-center justify-center"
          >
            <div
              className={`relative w-[85%] ml-6 max-w-3xl bg-[#ffffff] rounded-3xl shadow-lg px-6 md:px-8 py-6 flex items-center sm:w-[300px] lg:h-[250px] lg:w-[380px] transition-transform duration-300 ${activeIndex === index ? "scale-[1]" : "scale-[0.8] opacity-[0.7]"
                }`}
            >
              <div className="absolute -left-8 sm:-left-10 md:-left-12 top-1/2 transform -translate-y-1/2">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 object-cover rounded-full border-[6px] sm:border-[8px] border-[#F5F5DC]"
                />
              </div>
              <div className="ml-5 sm:ml-8 md:ml-8 pl-3 sm:pl-4 md:pl-5 text-left">
                <h1 className="ml-5 text-lg text-left sm:text-xl md:text-2xl font-semibold italic">
                  {testimonial.name}
                </h1>
                <p className="ml-5 mt-3 text-sm sm:text-base leading-relaxed text-gray-700">
                  "{testimonial.text}"
                </p>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {testimonials.length > 1 && (
        <div className="flex justify-center items-center mt-10 sm:mt-14 relative gap-8">
          <button
            className="text-black object-cover p-2 rounded-full"
            onClick={handleNext}
          >
            <img src={Spoon} alt="" className="w-20 h-9" />
          </button>
          <button
            className="text-black object-cover flex items-center p-2 rounded-full"
            onClick={handlePrev}
          >
            <img src={SpoonR} alt="" className="w-20 h-9" />
          </button>
        </div>
      )}
    </section>
  );
};

export default Feedback;